/**
 * Keyboard + input cheat-sheet overlay for the math editor. Opened with `?`
 * (when the equation canvas has focus, not a text field) or the help button.
 * Closed with `?` again, Escape, or a scrim click. The shortcut rows mirror
 * the live key handling in MathEditor.onKey and the Kb/math-editor.md article.
 */
import { useEffect } from "react";

interface CheatSheetProps {
  open: boolean;
  onClose: () => void;
}

const SHORTCUTS: { keys: string[]; does: string }[] = [
  { keys: ["^"], does: "Superscript / exponent on the previous atom  (a ^ 2 → a²)" },
  { keys: ["_"], does: "Subscript  (x _ i → xᵢ)" },
  { keys: ["\\"], does: "Command palette — insert a symbol or expand a structure (\\frac, \\sqrt, \\matrix, \\binom…)" },
  { keys: ["=", "+", "−", "×", "<", ">"], does: "Insert operator / relation  (type = + - * < >)" },
  { keys: ["a–z", "0–9", "( )", "[ ]"], does: "Typed straight into the equation" },
  { keys: ["Tab"], does: "Next structure slot (numerator → denominator); cycle drawer tabs at top level" },
  { keys: ["Esc"], does: "Step out of the current structure slot" },
  { keys: ["⇧", "↵"], does: "New line (multi-line / aligned math)" },
  { keys: ["&"], does: "Alignment point for aligned equations" },
  { keys: ["⌫"], does: "Delete the atom before the caret" },
  { keys: ["←", "→"], does: "Move the caret" },
  { keys: ["⌘/Ctrl", "Z"], does: "Undo   ·   ⌘/Ctrl ⇧ Z or ⌘/Ctrl Y to redo" },
  { keys: ["?"], does: "Show / hide this cheat sheet" },
];

const INPUTS: { name: string; desc: string }[] = [
  { name: "Buttons", desc: "Symbols + Structures tabs — click a tile when you don't recall the LaTeX name." },
  { name: "Raw LaTeX", desc: "The Source tab is editable — type LaTeX directly; the buttons + preview stay in sync." },
  { name: "AI", desc: "“Describe the math” box on the Source tab → generates the LaTeX for you." },
];

export function CheatSheet({ open, onClose }: CheatSheetProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "?") { e.preventDefault(); e.stopPropagation(); onClose(); }
    };
    // Capture phase so this wins over the editor's own window keydown handler.
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      data-testid="math-cheatsheet"
      onClick={onClose}
      style={{
        position: "absolute", inset: 0, zIndex: 60,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "color-mix(in srgb, var(--lilia-text, #0f172a) 35%, transparent)",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(560px, 100%)", maxHeight: "90%", overflow: "auto",
          background: "var(--lilia-surface, #fff)", color: "var(--lilia-text)",
          border: "1px solid var(--lilia-border)", borderRadius: 14,
          boxShadow: "var(--lilia-shadow-lg, 0 12px 40px rgba(0,0,0,.25))",
        }}
      >
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 18px", borderBottom: "1px solid var(--lilia-border)",
        }}>
          <span style={{ fontSize: 15, fontWeight: 700 }}>Math editor — shortcuts</span>
          <button
            type="button"
            data-testid="math-cheatsheet-close"
            onClick={onClose}
            aria-label="Close"
            style={{ border: "none", background: "none", color: "var(--lilia-text-muted)", fontSize: 18, cursor: "pointer", lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: "10px 18px 4px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--lilia-text-muted)" }}>
          Three ways to enter math
        </div>
        <div style={{ padding: "0 18px 10px", display: "flex", flexDirection: "column", gap: 6 }}>
          {INPUTS.map((it) => (
            <div key={it.name} style={{ fontSize: 13 }}>
              <b>{it.name}</b> <span style={{ color: "var(--lilia-text-secondary)" }}>— {it.desc}</span>
            </div>
          ))}
        </div>

        <div style={{ padding: "8px 18px 4px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--lilia-text-muted)", borderTop: "1px solid var(--lilia-border)" }}>
          Keyboard
        </div>
        <div style={{ padding: "4px 18px 16px" }}>
          {SHORTCUTS.map((s, i) => (
            <div key={i} style={{ display: "flex", gap: 12, alignItems: "baseline", padding: "5px 0", borderBottom: i < SHORTCUTS.length - 1 ? "1px solid color-mix(in srgb, var(--lilia-border) 55%, transparent)" : "none" }}>
              <div style={{ flex: "0 0 150px", display: "flex", gap: 4, flexWrap: "wrap" }}>
                {s.keys.map((k, j) => (
                  <kbd key={j} style={{
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    background: "var(--lilia-bg-muted, #f1f5f9)", border: "1px solid var(--lilia-border)",
                    borderBottomWidth: 2, borderRadius: 5, padding: "1px 6px", fontSize: 11.5, color: "var(--lilia-text)",
                  }}>{k}</kbd>
                ))}
              </div>
              <div style={{ fontSize: 12.5, color: "var(--lilia-text-secondary)" }}>{s.does}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
